import { cx } from './chunk-BIIWHS2O.js';
import { brandForegroundFor } from '@authe/fe-commons/theme';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import { KpiCard, Card, Table, TableHeader, TableRow, TableHead, TableBody, TableCell, Badge, HashDisplay, AddressDisplay } from '@authe/ui-core';
import { asString, fieldLabel } from '@authe/fe-commons/display';
import { coerceNumber } from '@authe/fe-commons/value';
import { layoutFlow, reportCellText, fitLabel } from '@authe/fe-commons/reporting';
import { formatCell } from '@authe/fe-commons/export';

function BrandHeader({ title, description, brand, className }) {
  const background = brand?.brandHsl ? `hsl(${brand.brandHsl})` : "hsl(var(--brand))";
  const foreground = brand?.brandHsl ? `hsl(${brandForegroundFor(brand.brandHsl)})` : "hsl(var(--brand-foreground))";
  return /* @__PURE__ */ jsxs(
    "header",
    {
      className: cx("flex items-center gap-4 rounded-lg px-5 py-4", className),
      style: { backgroundColor: background, color: foreground },
      children: [
        brand?.logoDataUrl ? /* @__PURE__ */ jsx("img", { src: brand.logoDataUrl, alt: "", className: "size-9 shrink-0 rounded-sm object-contain" }) : null,
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          brand?.name ? /* @__PURE__ */ jsx("div", { className: "text-xs font-medium uppercase tracking-wide opacity-80", children: brand.name }) : null,
          /* @__PURE__ */ jsx("h2", { className: "truncate text-lg font-semibold", children: title }),
          description ? /* @__PURE__ */ jsx("p", { className: "truncate text-sm opacity-90", children: description }) : null
        ] })
      ]
    }
  );
}

// src/preview/tones.ts
var TONE_TEXT = {
  default: "text-foreground",
  inflow: "text-flow-in",
  outflow: "text-flow-out",
  alert: "text-destructive",
  muted: "text-muted-foreground"
};
function toneText(tone) {
  return TONE_TEXT[tone ?? "default"] ?? TONE_TEXT.default;
}
function isReportTone(value) {
  return typeof value === "string" && value in TONE_TEXT;
}
var RenderersContext = React.createContext({});
function ReportRenderersProvider({ renderers, children }) {
  const parent = React.useContext(RenderersContext);
  const value = React.useMemo(() => ({ ...parent, ...renderers }), [parent, renderers]);
  return /* @__PURE__ */ jsx(RenderersContext.Provider, { value, children });
}
function rowText(row, key) {
  return key ? asString(row[key]) : void 0;
}
function toneOf(row, column) {
  const own = column.toneKey ? row[column.toneKey] : void 0;
  return isReportTone(own) ? own : column.tone;
}
function CellAbsent() {
  return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
}
var asText = ({ row, column }) => {
  const text = reportCellText(row, column);
  if (text === null) return /* @__PURE__ */ jsx(CellAbsent, {});
  if (column.format === "usd" && coerceNumber(row[column.key]) === void 0) {
    return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: text });
  }
  return text;
};
var DEFAULTS = {
  text: asText,
  number: asText,
  usd: asText,
  share: asText,
  chain: asText,
  datetime: asText,
  // A badge's weight says what it is: a finding (alert) is filled destructive, a
  // positive state (default) is filled, everything else is an outline.
  badge: ({ value, row, column }) => {
    if (!asString(value)) return /* @__PURE__ */ jsx(CellAbsent, {});
    const tone = toneOf(row, column);
    const variant = tone === "alert" ? "destructive" : tone === "default" ? "secondary" : "outline";
    return /* @__PURE__ */ jsx(Badge, { variant, children: String(value) });
  },
  address: ({ value, row, column }) => {
    const address = asString(value);
    if (!address) return /* @__PURE__ */ jsx(CellAbsent, {});
    return /* @__PURE__ */ jsx(AddressDisplay, { address, chain: rowText(row, column.chainKey), label: rowText(row, column.labelKey), copyable: true });
  },
  hash: ({ value, row, column }) => {
    const hash = asString(value);
    if (!hash) return /* @__PURE__ */ jsx(CellAbsent, {});
    return /* @__PURE__ */ jsx(HashDisplay, { hash, chain: rowText(row, column.chainKey), copyable: true });
  }
};
function ReportCell({ row, column }) {
  const renderers = React.useContext(RenderersContext);
  const format = column.format ?? "text";
  const render = renderers[format] ?? DEFAULTS[format];
  const value = row[column.key];
  const tone = toneOf(row, column);
  const sub = rowText(row, column.subKey);
  return /* @__PURE__ */ jsxs("span", { className: cx("inline-flex flex-col", column.align === "right" && "items-end", tone && toneText(tone)), children: [
    /* @__PURE__ */ jsx("span", { children: render({ value, row, column }) }),
    sub ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: sub }) : null
  ] });
}
function Value({ item }) {
  if (item.value === null) {
    return /* @__PURE__ */ jsx("span", { className: "text-base font-normal text-muted-foreground", children: item.absentReason ?? "Not stated" });
  }
  return /* @__PURE__ */ jsxs("span", { className: cx("flex flex-col", item.lead && "text-3xl"), children: [
    /* @__PURE__ */ jsx("span", { className: toneText(item.tone), children: item.value }),
    item.secondary ? /* @__PURE__ */ jsx("span", { className: "text-sm font-normal text-muted-foreground", children: item.secondary }) : null
  ] });
}
function KpiSectionView({ section }) {
  return /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4", children: section.items.map((item, i) => /* @__PURE__ */ jsx(
    KpiCard,
    {
      label: item.label,
      value: /* @__PURE__ */ jsx(Value, { item }),
      helperText: item.caption ?? item.helperText,
      positiveIsGood: item.positiveIsGood ?? true,
      className: cx(item.lead && "sm:col-span-2", "print:break-inside-avoid"),
      delta: item.deltaPct === void 0 ? void 0 : { value: item.deltaPct, period: item.period ?? "" }
    },
    item.id ?? `${item.label}-${i}`
  )) });
}
function TableSectionView({ section }) {
  const columns = section.columns.length > 0 ? section.columns : Object.keys(section.rows[0] ?? {}).map((key) => ({ key }));
  return /* @__PURE__ */ jsxs(Card, { className: "overflow-x-auto", children: [
    /* @__PURE__ */ jsxs(Table, { className: "w-full border-collapse text-sm", children: [
      /* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsx(TableRow, { className: "border-b text-left text-muted-foreground", children: columns.map((col) => /* @__PURE__ */ jsx(TableHead, { className: cx("px-3 py-2 font-medium", col.align === "right" && "text-right"), children: col.header ?? fieldLabel(col.key) }, col.key)) }) }),
      /* @__PURE__ */ jsx(TableBody, { children: section.rows.map((row, r) => /* @__PURE__ */ jsx(TableRow, { className: "border-b last:border-0 print:break-inside-avoid", children: columns.map((col) => /* @__PURE__ */ jsx(TableCell, { className: cx("px-3 py-2 align-top tabular-nums", col.align === "right" && "text-right"), children: col.format ? /* @__PURE__ */ jsx(ReportCell, { row, column: col }) : formatCell(row[col.key]) }, col.key)) }, r)) })
    ] }),
    section.rows.length === 0 ? /* @__PURE__ */ jsx("p", { className: "px-3 py-6 text-center text-sm text-muted-foreground", children: section.empty ?? "No rows" }) : null
  ] });
}
function NarrativeSectionView({ section }) {
  const paragraphs = section.body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return /* @__PURE__ */ jsx("div", { className: "prose prose-sm max-w-none text-foreground", children: paragraphs.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "No content" }) : paragraphs.map((p, i) => /* @__PURE__ */ jsx("p", { className: "leading-relaxed text-muted-foreground", children: p }, i)) });
}
var KICKER_PX = 9;
var LABEL_PX = 11;
var DETAIL_PX = 10;
var INSET = 9;
var LINE = 13;
function NodeText({ node }) {
  const width = node.w - INSET * 2;
  const lines = [
    node.kicker ? { key: "k", cls: "fill-muted-foreground uppercase tracking-wider", px: KICKER_PX, text: node.kicker.toUpperCase(), budget: width * 0.85 } : null,
    { key: "l", cls: "fill-foreground font-semibold", px: LABEL_PX, text: node.label, budget: width },
    node.detail ? { key: "d", cls: "fill-muted-foreground", px: DETAIL_PX, text: node.detail, budget: width } : null
  ].filter((l) => l !== null);
  const top = node.y + node.h / 2 - (lines.length - 1) * LINE / 2;
  return /* @__PURE__ */ jsx(Fragment, { children: lines.map((line, i) => /* @__PURE__ */ jsx(
    "text",
    {
      x: node.x + INSET,
      y: top + i * LINE,
      dominantBaseline: "middle",
      className: cx("font-mono", line.cls, node.tone === "alarming" && line.key === "k" && "fill-destructive"),
      style: { fontSize: line.px },
      children: fitLabel(line.text, line.budget, line.px)
    },
    line.key
  )) });
}
function FlowSectionView({ section }) {
  const layout = React.useMemo(
    () => layoutFlow({ sources: section.sources, destinations: section.destinations, center: section.center }),
    [section.sources, section.destinations, section.center]
  );
  if (!section.sources.length && !section.destinations.length) {
    return /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: section.empty ?? "No money moved to or from the subject." });
  }
  return /* @__PURE__ */ jsxs(Card, { className: "overflow-x-auto p-4 print:break-inside-avoid", children: [
    section.columnLabels ? /* @__PURE__ */ jsxs("div", { className: "mb-2 grid min-w-[760px] grid-cols-[22%_1fr_19%_1fr_22%] text-xs font-medium uppercase tracking-wide text-muted-foreground print:min-w-0", "aria-hidden": "true", children: [
      /* @__PURE__ */ jsx("span", { children: section.columnLabels.source }),
      /* @__PURE__ */ jsx("span", { className: "col-start-3 text-center", children: section.columnLabels.center }),
      /* @__PURE__ */ jsx("span", { className: "col-start-5 text-right", children: section.columnLabels.destination })
    ] }) : null,
    /* @__PURE__ */ jsxs(
      "svg",
      {
        viewBox: `0 0 ${layout.width} ${layout.height}`,
        role: "img",
        "aria-label": section.description,
        preserveAspectRatio: "xMidYMin meet",
        className: "block h-auto w-full min-w-[760px] print:min-w-0",
        children: [
          /* @__PURE__ */ jsx("g", { children: layout.ribbons.map((ribbon) => /* @__PURE__ */ jsx(
            "path",
            {
              d: ribbon.d,
              className: cx(
                ribbon.side === "source" ? "fill-flow-in" : "fill-flow-out",
                // A thin ribbon is a counterparty drawn at the minimum band — an
                // unpriced one — and needs more ink to be seen at all.
                ribbon.thickness <= 3 ? "opacity-70" : "opacity-35"
              )
            },
            ribbon.id
          )) }),
          layout.nodes.map((node) => /* @__PURE__ */ jsxs("g", { children: [
            /* @__PURE__ */ jsx(
              "rect",
              {
                x: node.x,
                y: node.y,
                width: node.w,
                height: node.h,
                rx: 6,
                className: cx(
                  "stroke-1",
                  node.side === "center" ? "fill-primary/10 stroke-primary" : node.tone === "alarming" ? "fill-card stroke-destructive" : "fill-card stroke-border"
                ),
                strokeDasharray: node.tone === "unknown" ? "4 3" : void 0
              }
            ),
            /* @__PURE__ */ jsx(NodeText, { node })
          ] }, `${node.side}:${node.id}`))
        ]
      }
    )
  ] });
}
function TimelineSectionView({ section }) {
  if (!section.items.length) {
    return /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: section.empty ?? "No steps." });
  }
  return /* @__PURE__ */ jsx(Card, { className: "divide-y", children: /* @__PURE__ */ jsx("ol", { children: section.items.map((item, i) => /* @__PURE__ */ jsxs("li", { className: "grid grid-cols-[2.5rem_1fr_auto] gap-x-4 gap-y-1 px-4 py-3 print:break-inside-avoid", children: [
    /* @__PURE__ */ jsx("span", { className: "font-mono text-sm font-semibold text-primary", children: String(i + 1).padStart(2, "0") }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold text-foreground", children: item.title || "Untitled step" }),
    /* @__PURE__ */ jsx("span", { className: cx("text-right text-xs", toneText(item.statusTone ?? "muted")), children: item.status }),
    item.note ? /* @__PURE__ */ jsx("p", { className: "col-start-2 col-end-4 max-w-prose whitespace-pre-wrap text-sm text-muted-foreground", children: item.note }) : null,
    item.tags?.length || item.chains?.length ? /* @__PURE__ */ jsxs("span", { className: "col-start-2 col-end-4 flex flex-wrap gap-1.5", children: [
      item.tags?.map((tag) => /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: tag }, `t:${tag}`)),
      item.chains?.map((chain) => /* @__PURE__ */ jsx(Badge, { variant: "outline", children: chain }, `c:${chain}`))
    ] }) : null
  ] }, item.id)) }) });
}
function FactsSectionView({ section }) {
  if (!section.items.length) {
    return /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: section.empty ?? "Nothing to state." });
  }
  return /* @__PURE__ */ jsx(Card, { className: "p-4", children: /* @__PURE__ */ jsx("dl", { className: "grid grid-cols-1 gap-x-8 gap-y-1 text-sm sm:grid-cols-2", children: section.items.map((item) => /* @__PURE__ */ jsxs("div", { className: "flex justify-between gap-4 border-b border-dashed py-1.5 last:border-0", children: [
    /* @__PURE__ */ jsx("dt", { className: "text-muted-foreground", children: item.label }),
    /* @__PURE__ */ jsx("dd", { className: "text-right font-medium tabular-nums text-foreground", children: item.value })
  ] }, item.label)) }) });
}
var ChartSectionView = React.lazy(() => import('./ChartSectionView-L4CXCA3W.js'));
function ChartFallback() {
  return /* @__PURE__ */ jsx("div", { className: "h-64 animate-pulse rounded-lg border bg-muted/40", "aria-hidden": true });
}
function SectionBody({ section }) {
  switch (section.kind) {
    case "kpi":
      return /* @__PURE__ */ jsx(KpiSectionView, { section });
    case "table":
      return /* @__PURE__ */ jsx(TableSectionView, { section });
    case "narrative":
      return /* @__PURE__ */ jsx(NarrativeSectionView, { section });
    case "divider":
      return /* @__PURE__ */ jsx("hr", { className: "border-border" });
    case "flow":
      return /* @__PURE__ */ jsx(FlowSectionView, { section });
    case "timeline":
      return /* @__PURE__ */ jsx(TimelineSectionView, { section });
    case "facts":
      return /* @__PURE__ */ jsx(FactsSectionView, { section });
    case "chart":
      return /* @__PURE__ */ jsx(React.Suspense, { fallback: /* @__PURE__ */ jsx(ChartFallback, {}), children: /* @__PURE__ */ jsx(ChartSectionView, { section }) });
    default: {
      const exhaustive = section;
      return /* @__PURE__ */ jsx(Fragment, { children: exhaustive });
    }
  }
}
function SectionShell({ section }) {
  if (section.kind === "divider") return /* @__PURE__ */ jsx(SectionBody, { section });
  return /* @__PURE__ */ jsxs("section", { className: "flex flex-col gap-3", "aria-label": section.title, children: [
    section.title || section.summary ? /* @__PURE__ */ jsxs("header", { className: "flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b pb-2 print:break-after-avoid", children: [
      section.title ? /* @__PURE__ */ jsx("h2", { className: "text-base font-semibold text-foreground", children: section.title }) : /* @__PURE__ */ jsx("span", {}),
      section.summary ? /* @__PURE__ */ jsx("span", { className: "text-sm tabular-nums text-muted-foreground", children: section.summary }) : null
    ] }) : null,
    /* @__PURE__ */ jsx(SectionBody, { section }),
    section.note ? /* @__PURE__ */ jsx("p", { className: "max-w-prose text-xs text-muted-foreground", children: section.note }) : null
  ] });
}
function DocumentHeader({ report, actions }) {
  return /* @__PURE__ */ jsxs("header", { className: "flex flex-col gap-4 border-b pb-5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground", children: [
        report.brand?.name ? /* @__PURE__ */ jsx("span", { className: "text-foreground", children: report.brand.name }) : null,
        report.brand?.name && report.kicker ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\xB7" }) : null,
        report.kicker ? /* @__PURE__ */ jsx("span", { children: report.kicker }) : null,
        report.classifier ? /* @__PURE__ */ jsx("span", { className: "rounded-sm border px-1.5 py-0.5 text-3xs tracking-wider text-foreground", children: report.classifier }) : null
      ] }),
      actions ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center gap-2 print:hidden", children: actions }) : null
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsx("h1", { className: "text-2xl font-semibold tracking-tight text-foreground [text-wrap:balance]", children: report.name }),
      report.description ? /* @__PURE__ */ jsx("p", { className: "max-w-prose text-sm text-muted-foreground", children: report.description }) : null
    ] }),
    report.meta?.length ? /* @__PURE__ */ jsx("dl", { className: "grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4", children: report.meta.map((fact) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
      /* @__PURE__ */ jsx("dt", { className: "text-xs uppercase tracking-wide text-muted-foreground", children: fact.label }),
      /* @__PURE__ */ jsx("dd", { className: "font-medium tabular-nums text-foreground", children: fact.value })
    ] }, fact.label)) }) : null
  ] });
}
function ReportPreview({ report, className, hideHeader, actions, renderers }) {
  const documentHeader = Boolean(report.kicker || report.meta?.length);
  return /* @__PURE__ */ jsx(ReportRenderersProvider, { renderers, children: /* @__PURE__ */ jsxs("article", { className: cx("flex flex-col gap-8", className), children: [
    hideHeader ? null : documentHeader ? /* @__PURE__ */ jsx(DocumentHeader, { report, actions }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      actions ? /* @__PURE__ */ jsx("div", { className: "flex justify-end gap-2 print:hidden", children: actions }) : null,
      /* @__PURE__ */ jsx(BrandHeader, { title: report.name, description: report.description, brand: report.brand })
    ] }),
    report.status ? /* @__PURE__ */ jsx("p", { role: "status", className: cx("rounded-md border px-4 py-3 text-sm", toneText(report.status.tone)), children: report.status.text }) : null,
    report.sections.length === 0 ? /* @__PURE__ */ jsx("p", { className: "rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground", children: "This report has no sections yet." }) : report.sections.map((section) => /* @__PURE__ */ jsx(SectionShell, { section }, section.id)),
    report.footer ? /* @__PURE__ */ jsxs("footer", { className: "flex flex-wrap justify-between gap-2 border-t pt-3 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx("span", { children: report.footer.left }),
      report.footer.right ? /* @__PURE__ */ jsx("span", { children: report.footer.right }) : null
    ] }) : null
  ] }) });
}

export { BrandHeader, CellAbsent, FactsSectionView, FlowSectionView, KpiSectionView, NarrativeSectionView, ReportCell, ReportPreview, ReportRenderersProvider, TONE_TEXT, TableSectionView, TimelineSectionView, isReportTone, rowText, toneText };
//# sourceMappingURL=chunk-CS23GKGR.js.map
//# sourceMappingURL=chunk-CS23GKGR.js.map